<template>
  <div class="material-category">
    <div v-if="showConfirmDialog" class="confirm-dialog-overlay">
      <div class="confirm-dialog">
        <p>{{ $t('materialCategory.purchaseConfirm', { price: selectedMaterial.price }) }}</p>
        <p class="points-status">{{ $t('materialCategory.currentPoints') }}: {{ userPoints }}</p>
        <div class="dialog-buttons">
          <button class="cancel-btn" type="button" :disabled="purchasing" @click="showConfirmDialog = false">{{ $t('materialCategory.cancel') }}</button>
          <button class="confirm-btn" type="button" :disabled="purchasing" @click="confirmPurchase">{{ purchasing ? $t('materialCategory.processing') : $t('materialCategory.confirm') }}</button>
        </div>
      </div>
    </div>

    <header class="workspace-header category-header">
      <button class="back-button" type="button" @click="goBack" :aria-label="$t('materialAdmin.backToLibrary')">
        <span class="material-icons" aria-hidden="true">arrow_back_ios_new</span>
      </button>
      <div class="workspace-heading">
        <h1 class="workspace-title">{{ categoryTitle }}</h1>
        <p class="workspace-subtitle category-meta">
          <span>{{ categoryMediaType === 'video' ? uiLabels.video : uiLabels.audio }}</span>
          <template v-if="categoryDescription">
            <span aria-hidden="true">·</span>
            <span>{{ categoryDescription }}</span>
          </template>
        </p>
      </div>
    </header>

    <div class="course-layout" :class="{ 'no-collections': !collections.length }">
      <aside v-if="collections.length" class="collection-rail">
        <template v-for="group in collectionGroups" :key="group.key">
          <div v-if="group.label" class="rail-group-label">{{ group.label }}</div>
          <button
            v-for="collection in group.collections"
            :key="collection.id"
            :class="['collection-btn', { active: currentBook === collection.id }]"
            type="button"
            :title="collection.title"
            @click="selectCollection(collection.id)"
          >
            <img v-if="collection.youtubeAvatarUrl" class="collection-avatar" :src="collection.youtubeAvatarUrl" alt="" loading="lazy">
            <span v-else class="collection-index">{{ collectionIndex(collection) }}</span>
            <span class="collection-text">
              <span class="collection-title">{{ collection.title }}</span>
              <span v-if="collection.youtubeSubscriberCount" class="collection-subscribers">{{ subscriberText(collection) }}</span>
            </span>
          </button>
        </template>
      </aside>

      <main class="lesson-panel">
        <div class="panel-head">
          <div v-if="currentCollection" class="collection-intro">
            <img v-if="currentCollection.youtubeAvatarUrl" class="collection-intro-avatar" :src="currentCollection.youtubeAvatarUrl" alt="">
            <div class="collection-intro-text">
              <h3 class="collection-heading">{{ currentCollection.title }}<template v-if="testNumber"> · Test {{ testNumber }}</template></h3>
              <p v-if="currentCollection.youtubeSubscriberCount" class="collection-stats">{{ subscriberText(currentCollection) }}</p>
              <p v-if="currentCollection.description" class="collection-description">{{ currentCollection.description }}</p>
            </div>
          </div>
          <div class="filter-toolbar">
            <div class="panel-filters">
              <OptionSelect
                v-for="filter in visibleFilters"
                :key="filter.key"
                class="filter-select"
                :class="{ 'is-active': filter.value !== 'all' }"
                :model-value="filter.value"
                :options="filter.options"
                :open="openFilter === filter.key"
                :aria-label="filter.label"
                @toggle="openFilter = openFilter === filter.key ? null : filter.key"
                @close="closeFilters"
                @select="selectFilter(filter.key, $event)"
              >
                <template #value="{ option }">
                  <span class="filter-label">{{ filter.label }}{{ $t('materialCategory.filterSeparator') }}</span>{{ option?.label }}
                </template>
                <template #option="{ option }">
                  <span>{{ option.label }}</span>
                  <span class="filter-option-count">{{ option.count }}</span>
                </template>
              </OptionSelect>
            </div>
            <div class="filter-summary">
              <span role="status">{{ $t('materialCategory.resultCount', { count: filteredMaterials.length }) }}</span>
              <button v-if="hasActiveFilters" class="clear-filters" type="button" @click="clearFilters">{{ $t('materialCategory.clearFilters') }}</button>
            </div>
          </div>
        </div>

        <div class="material-grid-shell" v-loading="loading" :element-loading-text="$t('materialCategory.loading')">
          <div class="material-grid">
            <template v-for="(item, index) in paginatedMaterials" :key="item.id">
              <button :class="['material-item', { 'material-item--video': item.mediaType === 'video' }]" type="button" @click="handleItemClick(item)">
                <div class="item-image">
                  <MaterialCover :src="item.image" :alt="item.title" :artwork="item.sourceType === 'podcast'" />
                  <span class="day-badge">{{ lessonNumber(index) }}</span>
                  <span v-if="mediaFilters[1].count && mediaFilters[2].count" class="media-badge">
                    <span class="material-icons-outlined" aria-hidden="true">{{ item.mediaType === 'video' ? 'play_circle' : 'headphones' }}</span>
                    {{ item.mediaType === 'video' ? uiLabels.video : uiLabels.audio }}
                  </span>
                </div>
                <div class="item-content">
                  <h3>{{ item.title }}</h3>
                  <p v-if="item.youtube?.viewCount" class="item-stats">
                    {{ $t('materialCategory.views', { count: compactNumber(item.youtube.viewCount) }) }}<template v-if="item.youtube.publishedAt"> · {{ relativeDate(item.youtube.publishedAt) }}</template>
                  </p>
                  <p v-else-if="item.description" class="item-description">{{ item.description }}</p>
                  <div class="item-meta">
                    <span class="item-facts">
                      <span v-if="item.duration">{{ item.duration }}</span>
                      <span v-if="item.duration && item.cefrLevel" aria-hidden="true">·</span>
                      <span v-if="item.cefrLevel">{{ item.cefrLevel }}</span>
                    </span>
                    <span v-if="isPurchased(item)" class="item-status owned">
                      <span class="material-icons" aria-hidden="true">check</span>
                      {{ uiLabels.owned }}
                    </span>
                    <span v-else-if="freeUnlocksLeft" class="item-status free">{{ $t('materialCategory.free') }}</span>
                    <span v-else class="item-status locked">
                      <span class="material-icons-outlined" aria-hidden="true">lock</span>
                      {{ item.price }} pts
                    </span>
                  </div>
                </div>
              </button>
            </template>
            <p v-if="!loading && !filteredMaterials.length" class="empty-message">
              {{ $t('materialCategory.noResults') }}
            </p>
          </div>
        </div>

        <div v-if="totalPages > 1" class="pagination">
          <button
            :disabled="currentPage === 1"
            class="page-btn"
            type="button"
            @click="currentPage--"
          >
            <span class="material-icons">chevron_left</span>
          </button>

          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>

          <button
            :disabled="currentPage === totalPages"
            class="page-btn"
            type="button"
            @click="currentPage++"
          >
            <span class="material-icons">chevron_right</span>
          </button>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { db } from '../../firebase'
import { doc, getDoc } from 'firebase/firestore'

import { ElMessage } from 'element-plus'
import OptionSelect from '../common/OptionSelect.vue'
import MaterialCover from './MaterialCover.vue'
import { useViews } from '@/composables/views'
import { useModals } from '@/composables/modals'
import { apiPost } from '@/composables/useApi'
import { useMaterials } from '@/composables/useMaterials'
import { currentMaterialCategory, currentMaterialCollection } from '@/composables/materialSeo'
import { localizedRouteName } from '@/router'
import { getFallbackCategory } from '@/assets/material/materialCatalog'
import { fallbackMaterialCategory, materialItemTest, parseTestParam } from '@/assets/seo/pageMeta'
import { useLearnerProfile } from '@/composables/useLearnerProfile'
import { countBy, filterMaterials, levelOptions, topicOptions } from '@/utils/materialFilters'
import { formatCompactNumber, formatRelativeDate } from '@/utils/format'

// 与后端 FREE_MATERIAL_UNLOCKS 一致：账号解锁的前几个素材不扣点数，也不弹确认
const FREE_MATERIAL_UNLOCKS = 3

export default {
  name: 'MaterialCategory',
  components: { MaterialCover, OptionSelect },
  setup() {
    const { handleViewChange } = useViews()
    const { handleUpgrade } = useModals()
    const { profile } = useLearnerProfile()
    return { _handleViewChange: handleViewChange, handleUpgrade, profile }
  },
  data() {
    return {
      category: null,
      collections: [],
      currentBook: null,
      materialList: [],
      purchasedMaterials: [],
      signedIn: false,
      userPoints: 0,
      showConfirmDialog: false,
      purchasing: false,
      selectedMaterial: null,
      loading: false,
      currentPage: 1,
      itemsPerPage: 24,
      openFilter: null,
      ownershipFilter: 'all',
      mediaFilter: 'all',
      levelFilter: 'all',
      topicFilter: 'all'
    }
  },
  computed: {
    categoryId() {
      return this.$route.params.id
    },
    // 地址是 /material/ielts/12/test-6 时只显示这一套题
    testNumber() {
      return parseTestParam(this.$route.params.test)
    },
    initialCollectionId() {
      return this.$route.params.collectionId || null
    },
    categoryTitle() {
      return this.category?.title || 'Learning Materials'
    },
    categoryDescription() {
      return this.category?.description || ''
    },
    categoryMediaType() {
      return this.category?.sourceType === 'youtube' || this.categoryId?.startsWith('youtube-') ? 'video' : 'audio'
    },
    isChineseLocale() {
      return this.$i18n?.locale?.toLowerCase().startsWith('zh')
    },
    uiLabels() {
      const isChinese = this.isChineseLocale
      return isChinese
        ? {
            owned: '已拥有',
            mediaFilter: '类型',
            ownershipFilter: '状态',
            allMedia: '全部',
            video: '视频',
            audio: '音频',
            femaleHosts: '女性博主',
            maleHosts: '男性博主'
          }
        : {
            owned: 'Owned',
            mediaFilter: 'Type',
            ownershipFilter: 'Status',
            allMedia: 'All',
            video: 'Video',
            audio: 'Audio',
            femaleHosts: 'Women',
            maleHosts: 'Men'
          }
    },
    collectionGroups() {
      if (!this.collections.some(collection => collection.hostGender)) {
        return [{ key: 'all', label: '', collections: this.collections }]
      }
      return [
        { key: 'female', label: this.uiLabels.femaleHosts },
        { key: 'male', label: this.uiLabels.maleHosts }
      ].map(group => ({
        ...group,
        collections: this.collections.filter(collection => collection.hostGender === group.key)
      }))
    },
    orderedCollections() {
      return this.collectionGroups.flatMap(group => group.collections)
    },
    currentCollection() {
      return this.collections.find(collection => collection.id === this.currentBook) || null
    },
    freeUnlocksLeft() {
      return this.signedIn ? Math.max(0, FREE_MATERIAL_UNLOCKS - this.purchasedMaterials.length) : 0
    },
    purchasedInCurrentCollection() {
      return this.materialList.filter(item => this.isPurchased(item)).length
    },
    ownershipFilters() {
      const owned = this.purchasedInCurrentCollection
      return [
        { value: 'all', label: this.$t('materialCategory.filterAll'), count: this.materialList.length },
        { value: 'purchased', label: this.$t('materialCategory.filterPurchased'), count: owned },
        { value: 'unpurchased', label: this.$t('materialCategory.filterUnpurchased'), count: this.materialList.length - owned }
      ]
    },
    totalPages() {
      return Math.ceil(this.filteredMaterials.length / this.itemsPerPage)
    },
    mediaFilters() {
      return [
        { value: 'all', label: this.uiLabels.allMedia, count: this.materialList.length },
        { value: 'video', label: this.uiLabels.video, count: this.materialList.filter(item => item.mediaType === 'video').length },
        { value: 'audio', label: this.uiLabels.audio, count: this.materialList.filter(item => item.mediaType === 'audio').length }
      ]
    },
    levelCounts() {
      return countBy(this.materialList, 'cefrLevel')
    },
    levelFilters() {
      return levelOptions(this.levelCounts).map(value => ({
        value,
        label: value === 'all' ? this.$t('materialCategory.levelAll') : value,
        count: this.levelCounts[value]
      }))
    },
    topicFilters() {
      const counts = countBy(this.materialList, 'topics')
      return topicOptions(counts, this.profile.interests).map(value => ({
        value,
        label: value === 'all' ? this.$t('materialCategory.topicAll') : this.$t(`learnerProfile.topics.${value}`),
        count: counts[value]
      }))
    },
    visibleFilters() {
      return [
        { key: 'mediaFilter', label: this.uiLabels.mediaFilter, value: this.mediaFilter, options: this.mediaFilters, visible: this.mediaFilters[1].count && this.mediaFilters[2].count },
        { key: 'ownershipFilter', label: this.uiLabels.ownershipFilter, value: this.ownershipFilter, options: this.ownershipFilters, visible: true },
        { key: 'levelFilter', label: this.$t('materialCategory.levelFilter'), value: this.levelFilter, options: this.levelFilters, visible: this.levelFilters.length > 2 },
        { key: 'topicFilter', label: this.$t('materialCategory.topicFilter'), value: this.topicFilter, options: this.topicFilters, visible: this.topicFilters.length > 2 }
      ].filter(filter => filter.visible)
    },
    hasActiveFilters() {
      return [this.mediaFilter, this.ownershipFilter, this.levelFilter, this.topicFilter].some(value => value !== 'all')
    },
    filteredMaterials() {
      return filterMaterials(this.materialList, { level: this.levelFilter, topic: this.topicFilter }).filter(item => {
        const purchased = this.isPurchased(item)
        const matchesOwnership = this.ownershipFilter === 'all' ||
          (this.ownershipFilter === 'purchased' && purchased) ||
          (this.ownershipFilter === 'unpurchased' && !purchased)
        return matchesOwnership &&
          (this.mediaFilter === 'all' || item.mediaType === this.mediaFilter)
      })
    },
    paginatedMaterials() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredMaterials.slice(start, end)
    }
  },
  methods: {
    closeFilters() {
      this.openFilter = null
    },
    selectFilter(key, value) {
      this[key] = value
      this.closeFilters()
    },
    clearFilters() {
      this.closeFilters()
      this.mediaFilter = 'all'
      this.ownershipFilter = 'all'
      this.levelFilter = 'all'
      this.topicFilter = 'all'
    },
    getMaterialService() {
      const translate = (...args) => this.$t(...args)
      translate.locale = this.$i18n?.locale
      return useMaterials(translate)
    },
    getCurrentUser() {
      const auth = getAuth()
      if (auth.currentUser) return Promise.resolve(auth.currentUser)

      return new Promise(resolve => {
        const unsubscribe = onAuthStateChanged(auth, user => {
          unsubscribe()
          resolve(user)
        })
      })
    },
    isPurchased(item) {
      const { getPurchaseKey } = this.getMaterialService()
      return this.purchasedMaterials.includes(getPurchaseKey(this.categoryId, item.id))
    },
    selectCollection(collectionId) {
      this.$router.push({
        name: localizedRouteName('MaterialCollection', this.$route.meta.locale),
        params: { id: this.categoryId, collectionId }
      })
    },
    compactNumber(value) {
      return formatCompactNumber(value, this.$i18n.locale)
    },
    relativeDate(isoDate) {
      return formatRelativeDate(isoDate, this.$i18n.locale)
    },
    subscriberText(collection) {
      return this.$t('materialCategory.subscribers', { count: this.compactNumber(collection.youtubeSubscriberCount) })
    },
    collectionIndex(collection) {
      const index = this.orderedCollections.findIndex(entry => entry.id === collection.id)
      return index >= 0 ? index + 1 : ''
    },
    lessonNumber(index) {
      return (this.currentPage - 1) * this.itemsPerPage + index + 1
    },
    async loadCategoryData() {
      const { fetchMaterialCategory, fetchMaterialCollections } = this.getMaterialService()
      this.category = await fetchMaterialCategory(this.categoryId)
      this.collections = await fetchMaterialCollections(this.categoryId)
      const requestedCollection = this.initialCollectionId
      this.currentBook = this.collections.some(collection => collection.id === requestedCollection)
        ? requestedCollection
        : this.collections[0]?.id || null
    },
    async fetchMaterials() {
      if (!this.currentBook) return
      this.loading = true
      try {
        const { fetchMaterialItems } = this.getMaterialService()
        const items = await fetchMaterialItems({
          categoryId: this.categoryId,
          collectionId: this.currentBook
        })
        const seoCategory = getFallbackCategory(this.categoryId) || fallbackMaterialCategory(this.categoryId)
        this.materialList = this.testNumber
          ? items.filter(item => materialItemTest(seoCategory, this.currentBook, item)?.test === this.testNumber)
          : items
        // 默认看全部等级，由用户自己筛选
        this.levelFilter = 'all'
        this.topicFilter = 'all'
      } catch (error) {
        ElMessage.error(this.$t('materialCategory.loadFailed'))
      } finally {
        this.loading = false
      }
      await this.playRequestedItem()
    },
    // 地址带 ?play=<素材 ID> 时（示范课结束推荐的素材）直接打开这一条
    async playRequestedItem() {
      const itemId = this.$route.query.play
      if (!itemId) return
      const { play, ...query } = this.$route.query
      this.$router.replace({ query })
      const item = this.materialList.find(entry => entry.id === itemId)
      if (!item) return
      await this.userDataReady
      await this.handleItemClick(item)
    },
    async loadUserData() {
      try {
        const user = await this.getCurrentUser()
        if (!user) return

        const userDoc = await getDoc(doc(db, 'users', user.uid))
        if (userDoc.exists()) {
          const userData = userDoc.data()
          this.signedIn = true
          this.purchasedMaterials = userData.purchasedMaterials || []
          this.userPoints = userData.quota?.remaining ?? userData.monthlyUsage?.remaining ?? 0
        }
      } catch {}
    },
    async purchaseMaterial(item, { free = false } = {}) {
      try {
        const user = getAuth().currentUser
        if (!user) {
          ElMessage.error(this.$t('materialCategory.pleaseLogin'))
          return false
        }

        const { getPurchaseKey, rememberMaterialDetail } = this.getMaterialService()
        const result = await apiPost('/api/materials/purchase', {
          categoryId: this.categoryId,
          collectionId: this.currentBook,
          itemId: item.id
        })
        rememberMaterialDetail({
          categoryId: this.categoryId,
          collectionId: this.currentBook,
          itemId: item.id,
          item: result.item
        })

        this.userPoints = result.remaining
        const purchaseKey = result.purchaseKey || getPurchaseKey(this.categoryId, item.id)
        if (!this.purchasedMaterials.includes(purchaseKey)) {
          this.purchasedMaterials.push(purchaseKey)
        }
        if (!free) ElMessage.success(this.$t('materialCategory.purchaseSuccess'))
        else if (this.freeUnlocksLeft) ElMessage.success(this.$t('materialCategory.freeUnlocked', { count: this.freeUnlocksLeft }))
        else ElMessage.success(this.$t('materialCategory.freeUnlockedLast'))
        return result
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialCategory.purchaseFailed'))
        return false
      }
    },
    async handleItemClick(item) {
      if (this.purchasing) return
      if (this.isPurchased(item)) {
        await this.openMaterial(item)
      } else if (this.freeUnlocksLeft) {
        this.purchasing = true
        this.loading = true
        const unlocked = await this.purchaseMaterial(item, { free: true })
        this.loading = false
        if (unlocked) await this.openMaterial(item, { mediaUrl: unlocked.url })
        this.purchasing = false
      } else {
        this.selectedMaterial = item
        this.showConfirmDialog = true
      }
    },
    // 刚买的素材，购买接口已带回播放地址（mediaUrl），不用再签一次
    async openMaterial(item, { mediaUrl: purchasedMediaUrl = '' } = {}) {
      this.loading = true
      try {
        const auth = getAuth()
        if (!auth.currentUser) return

        const { fetchMaterialDetail } = this.getMaterialService()
        // 列表里已有文件路径和视频 ID，播放地址和素材详情同时请求；播客音频地址在详情里，直接播原地址
        const youtubeVideoId = item.youtubeVideoId
        const externalAudio = item.sourceType === 'podcast'
        const [data, mediaUrl] = await Promise.all([
          fetchMaterialDetail({
            categoryId: this.categoryId,
            collectionId: this.currentBook,
            item
          }),
          youtubeVideoId || externalAudio || purchasedMediaUrl
            ? purchasedMediaUrl
            : apiPost('/api/get-signed-url', {
                filePath: item.filePath,
                categoryId: this.categoryId,
                collectionId: this.currentBook,
                itemId: item.id
              }).then(result => result.url)
        ])

        const audioTitle = data.title || item.title || data.name || item.id
        const audioName = data.description
          ? `${audioTitle} - ${data.description}`
          : audioTitle
        const audioData = {
          ...data,
          id: 'materials-' + this.categoryId,
          materialId: item.id,
          materialBook: this.currentBook,
          name: audioName,
          url: externalAudio ? data.audioUrl : mediaUrl,
          youtubeVideoId
        }

        this._handleViewChange('AudioPlayer', {
          audioSrc: audioData.url,
          youtubeVideoId: audioData.youtubeVideoId,
          cover: { src: item.image, artwork: externalAudio },
          audioName: audioData.name,
          audioId: audioData.id,
          taskType: audioData.type || 'stt',
          originalSubtitles: audioData.originalSubtitles,
          subtitles: audioData.subtitles,
          practiceSubtitles: audioData.practiceSubtitles,
          shortPracticeSubtitles: audioData.shortPracticeSubtitles,
          pauseSubtitles: audioData.pauseSubtitles,
          completeSubtitles: audioData.completeSubtitles,
          words: audioData.words,
          useSmartSplit: false,
          originalText: audioData.originalText,
          filePath: audioData.filePath,
          duration: audioData.durationSeconds || audioData.duration,
          materialId: audioData.materialId,
          materialBook: audioData.materialBook,
          allowAudioDownload: !externalAudio,
          isDemo: false,
          returnView: 'Material',
          returnParams: { categoryId: this.categoryId, collectionId: this.currentBook }
        })
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialCategory.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    async confirmPurchase() {
      if (!this.selectedMaterial) return
      if (this.userPoints < this.selectedMaterial.price) {
        this.handleUpgrade()
        this.showConfirmDialog = false
        return
      }

      // 弹窗保持到素材打开为止，遮罩挡住页面上的其他点击
      this.purchasing = true
      const purchased = await this.purchaseMaterial(this.selectedMaterial)
      if (purchased) {
        await this.openMaterial(this.selectedMaterial, { mediaUrl: purchased.url })
      }
      this.purchasing = false
      this.showConfirmDialog = false
    },
    goBack() {
      this.$router.push({ name: localizedRouteName('Material', this.$route.meta.locale) })
    }
  },
  async created() {
    this.userDataReady = this.loadUserData()
    await Promise.all([
      this.loadCategoryData(),
      this.userDataReady
    ])
  },
  mounted() {
    document.addEventListener('click', this.closeFilters)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeFilters)
  },
  async beforeRouteUpdate(to, from, next) {
    this.closeFilters()
    next()
    if (to.params.id !== from.params.id) {
      this.currentBook = null
      this.currentPage = 1
      await this.loadCategoryData()
    }
  },
  watch: {
    // 合集跟随地址：/material/:id 是第一个合集，/material/:id/:collectionId 是指定合集
    '$route.params'(params, previous) {
      if (params.id !== previous.id) return
      const requested = params.collectionId || this.collections[0]?.id || null
      if (requested !== this.currentBook && this.collections.some(collection => collection.id === requested)) {
        this.currentBook = requested
      }
    },
    category(category) {
      currentMaterialCategory.value = category
        ? { id: this.categoryId, titleEn: category.titleEn || category.title, titleZh: category.titleZh || category.title }
        : null
    },
    currentCollection: {
      immediate: true,
      handler(collection) {
        currentMaterialCollection.value = collection
          ? {
              categoryId: this.categoryId,
              id: collection.id,
              title: collection.titleEn || collection.title,
              titleZh: collection.titleZh
            }
          : null
      }
    },
    async testNumber() {
      this.currentPage = 1
      await this.fetchMaterials()
    },
    currentBook: {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue) return
        this.currentPage = 1
        this.mediaFilter = 'all'
        await this.fetchMaterials()
      }
    },
    ownershipFilter() {
      this.currentPage = 1
    },
    mediaFilter() {
      this.currentPage = 1
    },
    levelFilter() {
      this.currentPage = 1
    },
    topicFilter() {
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
.material-category {
  max-width: none;
  margin: 0;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
}

/* Header */
.category-header {
  justify-content: flex-start;
  align-items: center;
  gap: var(--space-4);
  min-width: 0;
}

.back-button {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 50%;
  color: var(--ink-primary);
  background: rgba(16, 24, 32, 0.07);
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.back-button .material-icons {
  font-size: 18px;
  margin-right: 2px;
}

.back-button:hover {
  background: rgba(16, 24, 32, 0.12);
}

.back-button:active {
  transform: scale(0.94);
}

.back-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.category-meta {
  display: flex;
  gap: 0.4em;
  min-width: 0;
}

.category-meta span:last-child {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Layout */
.course-layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: var(--space-6);
  height: var(--workspace-fill-height);
  min-height: 0;
}

.course-layout.no-collections {
  grid-template-columns: 1fr;
}

.collection-rail,
.material-grid {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.collection-rail:hover,
.material-grid:hover {
  scrollbar-color: rgba(16, 24, 32, 0.18) transparent;
}

.collection-rail::-webkit-scrollbar,
.material-grid::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.collection-rail::-webkit-scrollbar-thumb,
.material-grid::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: transparent;
}

.collection-rail:hover::-webkit-scrollbar-thumb,
.material-grid:hover::-webkit-scrollbar-thumb {
  background: rgba(16, 24, 32, 0.18);
}

/* Collection rail */
.collection-rail {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
  overflow-y: auto;
  padding-right: var(--space-2);
  border-right: 1px solid var(--border-subtle);
}

.rail-group-label {
  padding: var(--space-3) var(--space-3) var(--space-1);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 600;
}

.rail-group-label:first-child {
  padding-top: 0;
}

.collection-btn {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--space-3);
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-secondary);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.collection-btn:hover {
  color: var(--ink-primary);
  background: var(--surface-subtle);
}

.collection-btn.active {
  color: var(--ink-primary);
  background: var(--surface-active);
}

.collection-btn:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: -2px;
}

.collection-index {
  width: 20px;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.collection-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--surface-subtle);
  object-fit: cover;
}

.collection-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.collection-subscribers {
  overflow: hidden;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.4;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.collection-btn.active .collection-index {
  color: var(--signal-active-strong);
}

.collection-title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow-wrap: anywhere;
  font-size: var(--font-size-sm);
  font-weight: 500;
  line-height: 1.35;
}

.collection-btn.active .collection-title {
  font-weight: 600;
}

/* Lesson panel */
.lesson-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.panel-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.collection-heading {
  min-width: 0;
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-lg);
  font-weight: 600;
  line-height: 1.3;
}

.collection-intro {
  display: flex;
  flex: 1 1 220px;
  align-items: flex-start;
  gap: var(--space-3);
  min-width: 0;
}

.collection-intro-avatar {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--surface-subtle);
  object-fit: cover;
}

.collection-intro-text {
  min-width: 0;
}

.collection-stats {
  margin: 2px 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}


.collection-description {
  margin: 2px 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.filter-toolbar {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-1);
  max-width: 100%;
  margin-left: auto;
}

.panel-filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-2);
  min-width: 0;
}

.filter-select {
  width: auto;
  min-width: 116px;
  max-width: 220px;
}

.filter-select :deep(.option-select-control) {
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 36px;
  border-color: var(--border-default);
  font-weight: 500;
}

.filter-select.is-active :deep(.option-select-control) {
  background: var(--surface-active);
}

.filter-label {
  color: var(--ink-secondary);
  font-weight: 400;
}

.filter-select :deep(.option-select-menu) {
  box-sizing: border-box;
  min-width: 180px;
}

.filter-select:last-child :deep(.option-select-menu) {
  right: 0;
  left: auto;
}

.filter-select :deep(.option-select-item-label) {
  display: flex;
  flex: 1;
  justify-content: space-between;
  gap: var(--space-4);
}

.filter-option-count {
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.filter-summary {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-left: auto;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.clear-filters {
  min-height: 36px;
  padding: 0 var(--space-2);
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--signal-active-strong);
  font: inherit;
  cursor: pointer;
}

.clear-filters:hover {
  background: var(--surface-active);
}

.clear-filters:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

/* Material grid */
.material-grid-shell {
  position: relative;
  flex: 1;
  min-height: 220px;
  overflow: hidden;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  grid-auto-rows: max-content;
  align-content: start;
  gap: var(--space-4);
  height: 100%;
  min-height: 0;
  overflow: auto;
  padding: 2px var(--space-1) 2px 2px;
}

.material-item {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.material-item:hover {
  transform: translateY(-1px);
  border-color: var(--border-active);
  box-shadow: var(--shadow-lift);
}

.material-item:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

/* 音频和视频封面都按 16:9 完整显示 */
.item-image {
  position: relative;
  flex: 0 0 auto;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: var(--surface-subtle);
}

.day-badge,
.media-badge {
  position: absolute;
  top: var(--space-2);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  color: var(--color-text-inverse);
  background: rgba(16, 24, 32, 0.72);
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
}

.day-badge {
  left: var(--space-2);
  font-family: var(--font-family-data);
  font-variant-numeric: tabular-nums;
}

.media-badge {
  right: var(--space-2);
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}

.media-badge .material-icons-outlined {
  font-size: 13px;
}

.item-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding: var(--space-3);
}

.item-content h3 {
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--ink-primary);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.35;
}

.item-description {
  display: -webkit-box;
  margin: var(--space-1) 0 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: var(--ink-secondary);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.item-stats {
  margin: var(--space-1) 0 0;
  overflow: hidden;
  color: var(--ink-secondary);
  font-size: 0.8125rem;
  line-height: 1.45;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: auto;
  padding-top: var(--space-3);
  min-width: 0;
  font-size: var(--font-size-xs);
}

.item-facts {
  display: flex;
  gap: 0.35em;
  min-width: 0;
  overflow: hidden;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.item-status {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-left: auto;
  font-weight: 600;
  white-space: nowrap;
}

.item-status .material-icons,
.item-status .material-icons-outlined {
  font-size: 14px;
}

.item-status.owned {
  color: var(--signal-active-strong);
}

.item-status.free {
  color: var(--ink-primary);
}

.item-status.locked {
  color: var(--ink-secondary);
}

.empty-message {
  grid-column: 1 / -1;
  justify-self: center;
  margin: var(--space-8) 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--space-4);
  margin-top: var(--space-4);
}

.page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  color: var(--ink-primary);
  background: rgba(16, 24, 32, 0.06);
  cursor: pointer;
}

.page-btn:hover:not(:disabled) {
  background: rgba(16, 24, 32, 0.1);
}

.page-btn:disabled {
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.page-btn:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.page-info {
  min-width: 60px;
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
  text-align: center;
}

/* Purchase dialog */
.confirm-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-overlay);
}

.confirm-dialog {
  width: min(360px, calc(100vw - 2rem));
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
  text-align: center;
}

.points-status {
  margin: var(--space-4) 0;
  color: var(--ink-secondary);
}

.dialog-buttons {
  display: flex;
  justify-content: center;
  gap: var(--space-3);
}

.dialog-buttons button {
  height: 38px;
  padding: 0 var(--space-5);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.dialog-buttons button:disabled {
  border-color: var(--border-default);
  color: var(--ink-tertiary);
  background: var(--surface-subtle);
  cursor: not-allowed;
}

.cancel-btn {
  border: 1px solid var(--border-default);
  color: var(--ink-primary);
  background: var(--surface-panel);
}

.confirm-btn {
  border: 1px solid var(--ink-primary);
  color: var(--color-text-inverse);
  background: var(--ink-primary);
}

@media (prefers-reduced-motion: reduce) {
  .filter-select :deep(.option-select-control),
  .filter-select :deep(.option-select-arrow) {
    transition: none;
  }

  .back-button,
  .material-item {
    transition: none;
  }

  .material-item:hover,
  .back-button:active {
    transform: none;
  }
}

@media (max-width: 960px) {
  .course-layout {
    grid-template-columns: 1fr;
    gap: var(--space-4);
    height: auto;
  }

  .collection-rail {
    flex-direction: row;
    gap: var(--space-1);
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 0 var(--space-2);
    border-right: none;
  }

  .rail-group-label,
  .rail-group-label:first-child {
    flex: 0 0 auto;
    align-self: center;
    padding: 0 var(--space-2);
    white-space: nowrap;
  }

  .collection-btn {
    flex: 0 0 auto;
    grid-template-columns: auto auto;
    gap: var(--space-2);
    max-width: 220px;
    width: auto;
  }

  .collection-title {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .collection-avatar {
    width: 24px;
    height: 24px;
  }

  .collection-subscribers {
    display: none;
  }
}

@media (max-width: 639px) {
  .filter-toolbar {
    width: 100%;
  }

  .panel-filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .filter-select {
    width: 100%;
    max-width: none;
    min-width: 0;
  }

  .filter-select :deep(.option-select-control),
  .filter-select :deep(.option-select-item) {
    min-height: 44px;
  }

  .filter-select :deep(.option-select-menu) {
    min-width: 100%;
  }

  .filter-summary {
    justify-content: space-between;
    width: 100%;
    margin-left: 0;
  }

  .clear-filters {
    min-height: 44px;
  }
}

@media (max-width: 768px) {
  .category-header {
    flex-direction: row;
  }

  .material-grid-shell {
    overflow: visible;
  }

  .material-grid {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
    padding: 0;
  }

  .material-item {
    display: grid;
    grid-template-columns: 104px minmax(0, 1fr);
  }

  .item-image {
    height: 100%;
    min-height: 104px;
    aspect-ratio: auto;
  }

  .material-item--video {
    display: flex;
  }

  .material-item--video .item-image {
    height: auto;
    min-height: 0;
    aspect-ratio: 16 / 9;
  }

  .item-description {
    -webkit-line-clamp: 1;
  }
}

@media (max-width: 430px) {
  .category-header {
    gap: var(--space-3);
  }

  .material-item {
    grid-template-columns: 92px minmax(0, 1fr);
  }
}
</style>
